import React, { useState, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';
import axios from 'axios';
import { useTestMasters } from '../../hooks/useTestMasters';

const TACTIC_CATEGORIES = [
  'Exercise',
  'Diet & Nutrition',
  'Life Style',
  'Supplements'
];

const OPTION_TYPES = {
  TWO_OPTION: [
    { color: '#27AE60', label: 'Done' },
    { color: '#E74C3C', label: 'Not done' }
  ],
  THREE_OPTION: [
    { color: '#27AE60', label: 'All days' },
    { color: '#F39C12', label: 'Some days' },
    { color: '#E74C3C', label: 'None' }
  ]
};

const TacticsModal = ({ isOpen, onClose, onSave, tactic, mode }) => {
  const { allTestMasters } = useTestMasters();
  const [healthGoals, setHealthGoals] = useState([]);
  
  // Get unique biomarkers from all test masters (113+ items)
  const BIOMARKERS = React.useMemo(() => {
    if (!allTestMasters || allTestMasters.length === 0) return [];
    const uniqueBiomarkers = new Set();
    allTestMasters.forEach(test => {
      if (test.biomarker_name) {
        uniqueBiomarkers.add(test.biomarker_name);
      }
    });
    return Array.from(uniqueBiomarkers).sort();
  }, [allTestMasters]);
  
  const [formData, setFormData] = useState({
    tactic_name: '',
    tactic_slug: '',
    tactic_category: '',
    impacted_biomarkers: [],
    impacted_goals: [],
    description: '',
    examples: '',
    how_to_do_it: '',
    common_mistakes: '',
    cautions: '',
    when_to_recommend: '',
    when_not_to_recommend: '',
    references: '',
    tactic_options: []
  });

  const [biomarkerSearch, setBiomarkerSearch] = useState('');
  const [showBiomarkerDropdown, setShowBiomarkerDropdown] = useState(false);
  const [selectedOptionType, setSelectedOptionType] = useState('');

  // Helper function to generate slug from name
  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s]/g, '') // Remove special characters
      .replace(/\s+/g, '_'); // Replace spaces with underscores
  };

  // Fetch health goals from API
  useEffect(() => {
    const fetchHealthGoals = async () => {
      try {
        const response = await axios.get('https://vitaldev.vitalinsights.in/health/health-goals');
        if (response.data && response.data.data) {
          setHealthGoals(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching health goals:', error);
        setHealthGoals([]);
      }
    };

    if (isOpen) {
      fetchHealthGoals();
    }
  }, [isOpen]);

  useEffect(() => {
    if (mode === 'edit' && tactic) {
      const tacticOptions = tactic.tactic_options 
        ? (typeof tactic.tactic_options === 'string' ? JSON.parse(tactic.tactic_options) : tactic.tactic_options)
        : [];
      
      setFormData({
        tactic_name: tactic.tactic_name || '',
        tactic_slug: tactic.tactic_slug || '',
        tactic_category: tactic.tactic_category || '',
        impacted_biomarkers: tactic.impacted_biomarkers || [],
        impacted_goals: tactic.impacted_goals || [],
        description: tactic.description || '',
        examples: tactic.examples || '',
        how_to_do_it: tactic.how_to_do_it || '',
        common_mistakes: tactic.common_mistakes || '',
        cautions: tactic.cautions || '',
        when_to_recommend: tactic.when_to_recommend || '',
        when_not_to_recommend: tactic.when_not_to_recommend || '',
        references: tactic.references || '',
        tactic_options: tacticOptions
      });
      
      // Determine option type from loaded options
      if (tacticOptions.length === 2) {
        setSelectedOptionType('TWO_OPTION');
      } else if (tacticOptions.length === 3) {
        setSelectedOptionType('THREE_OPTION');
      }
    } else {
      setFormData({
        tactic_name: '',
        tactic_slug: '',
        tactic_category: '',
        impacted_biomarkers: [],
        impacted_goals: [],
        description: '',
        examples: '',
        how_to_do_it: '',
        common_mistakes: '',
        cautions: '',
        when_to_recommend: '',
        when_not_to_recommend: '',
        references: '',
        tactic_options: []
      });
      setSelectedOptionType('');
    }
    setBiomarkerSearch('');
  }, [mode, tactic, isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: value
      };
      
      // Auto-generate slug when tactic_name changes and slug is empty
      if (name === 'tactic_name' && !prev.tactic_slug) {
        updated.tactic_slug = generateSlug(value);
      }
      
      return updated;
    });
  };

  const handleAddBiomarker = (biomarker) => {
    const biomarkerName = typeof biomarker === 'object' ? biomarker.name : biomarker;
    const alreadyExists = formData.impacted_biomarkers.some(b => {
      const name = typeof b === 'object' ? b.name : b;
      return name === biomarkerName;
    });
    
    if (!alreadyExists) {
      setFormData(prev => ({
        ...prev,
        impacted_biomarkers: [...prev.impacted_biomarkers, biomarkerName]
      }));
    }
    setBiomarkerSearch('');
    setShowBiomarkerDropdown(false);
  };

  const handleRemoveBiomarker = (biomarker) => {
    const biomarkerName = typeof biomarker === 'object' ? biomarker.name : biomarker;
    setFormData(prev => ({
      ...prev,
      impacted_biomarkers: prev.impacted_biomarkers.filter(b => {
        const name = typeof b === 'object' ? b.name : b;
        return name !== biomarkerName;
      })
    }));
  };

  const handleToggleGoal = (goal) => {
    const goalName = typeof goal === 'object' ? goal.name : goal;
    setFormData(prev => ({
      ...prev,
      impacted_goals: prev.impacted_goals.some(g => {
        const name = typeof g === 'object' ? g.name : g;
        return name === goalName;
      })
        ? prev.impacted_goals.filter(g => {
            const name = typeof g === 'object' ? g.name : g;
            return name !== goalName;
          })
        : [...prev.impacted_goals, goalName]
    }));
  };

  const filteredBiomarkers = BIOMARKERS.filter(b =>
    b.toLowerCase().includes(biomarkerSearch.toLowerCase()) &&
    !formData.impacted_biomarkers.some(existing => {
      const name = typeof existing === 'object' ? existing.name : existing;
      return name === b;
    })
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.tactic_name.trim()) {
      alert('Please enter a tactic name');
      return;
    }

    onSave(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-screen overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white">
          <h2 className="text-2xl font-bold text-gray-800">
            {mode === 'create' ? 'Add New Tactic' : 'Edit Tactic'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <FaTimes size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Tactic Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tactic Name *
            </label>
            <input
              type="text"
              name="tactic_name"
              value={formData.tactic_name}
              onChange={handleInputChange}
              placeholder="e.g., Daily Vitamin D3 (2000 IU) & K2 Supplementation"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Tactic Slug */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tactic Slug
            </label>
            <input
              type="text"
              name="tactic_slug"
              value={formData.tactic_slug}
              onChange={handleInputChange}
              placeholder="e.g., daily_vitamin_d3_k2_supplementation"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">Auto-generated from tactic name, but you can edit it</p>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tactic Category
            </label>
            <select
              name="tactic_category"
              value={formData.tactic_category}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">Select a category</option>
              {TACTIC_CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          {/* Option Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Response Options Type
            </label>
            <div className="space-y-2">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="optionType"
                  value="TWO_OPTION"
                  checked={selectedOptionType === 'TWO_OPTION'}
                  onChange={(e) => {
                    setSelectedOptionType(e.target.value);
                    setFormData(prev => ({
                      ...prev,
                      tactic_options: OPTION_TYPES.TWO_OPTION
                    }));
                  }}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Two Options (Done / Not done)</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="optionType"
                  value="THREE_OPTION"
                  checked={selectedOptionType === 'THREE_OPTION'}
                  onChange={(e) => {
                    setSelectedOptionType(e.target.value);
                    setFormData(prev => ({
                      ...prev,
                      tactic_options: OPTION_TYPES.THREE_OPTION
                    }));
                  }}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Three Options (All days / Some days / None)</span>
              </label>
            </div>
            {selectedOptionType && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                <p className="text-xs font-medium text-gray-600 mb-2">Preview:</p>
                <div className="flex gap-2 flex-wrap">
                  {formData.tactic_options.map((option, idx) => (
                    <div
                      key={idx}
                      className="px-3 py-1 rounded-full text-white text-sm font-medium"
                      style={{ backgroundColor: option.color }}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Impacted Biomarkers */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Impacted Biomarkers
            </label>
            <div className="relative mb-3">
              <input
                type="text"
                value={biomarkerSearch}
                onChange={(e) => setBiomarkerSearch(e.target.value)}
                onFocus={() => setShowBiomarkerDropdown(true)}
                onBlur={() => setTimeout(() => setShowBiomarkerDropdown(false), 200)}
                placeholder="Search and add biomarkers..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              {showBiomarkerDropdown && (
                <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto z-10">
                  {filteredBiomarkers.length > 0 ? (
                    filteredBiomarkers.map(biomarker => (
                      <button
                        key={biomarker}
                        type="button"
                        onClick={() => handleAddBiomarker(biomarker)}
                        className="w-full text-left px-4 py-2 hover:bg-blue-50 transition-colors"
                      >
                        {biomarker}
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-2 text-gray-500">No biomarkers found</div>
                  )}
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.impacted_biomarkers.map((biomarker, idx) => {
                const biomarkerName = typeof biomarker === 'object' ? biomarker.name : biomarker;
                return (
                  <div
                    key={idx}
                    className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                  >
                    {biomarkerName}
                    <button
                      type="button"
                      onClick={() => handleRemoveBiomarker(biomarker)}
                      className="text-blue-600 hover:text-blue-800 font-bold"
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Impacted Goals */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Impacted Goals
            </label>
            <div className="grid grid-cols-2 gap-3">
              {healthGoals.length > 0 ? (
                healthGoals.map(goal => {
                  const goalName = goal.goal_name;
                  const isChecked = formData.impacted_goals.some(g => {
                    const name = typeof g === 'object' ? g.name : g;
                    return name === goalName;
                  });
                  return (
                    <label key={goal.id} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleToggleGoal(goalName)}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700">{goalName}</span>
                    </label>
                  );
                })
              ) : (
                <div className="col-span-2 text-gray-500 text-sm">Loading health goals...</div>
              )}
            </div>
          </div>

          {/* What It Is / Specifics */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              What It Is / Specifics
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Describe what this tactic is and its specifics..."
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* How It Will Help */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              How It Will Help
            </label>
            <textarea
              name="examples"
              value={formData.examples}
              onChange={handleInputChange}
              placeholder="Explain how this tactic will help and provide examples..."
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* How To Start / How To Do It */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              How To Start / How To Do It
            </label>
            <textarea
              name="how_to_do_it"
              value={formData.how_to_do_it}
              onChange={handleInputChange}
              placeholder="Provide step-by-step instructions on how to start and implement this tactic..."
              rows="4"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Common Mistakes to Avoid */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Common Mistakes to Avoid
            </label>
            <textarea
              name="common_mistakes"
              value={formData.common_mistakes}
              onChange={handleInputChange}
              placeholder="List common mistakes people make when implementing this tactic..."
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Caution / When To Avoid / Contraindications */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Caution / When To Avoid / Contraindications
            </label>
            <textarea
              name="cautions"
              value={formData.cautions}
              onChange={handleInputChange}
              placeholder="Describe cautions, contraindications, or when to avoid this tactic..."
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* When to Recommend */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              When to Recommend
            </label>
            <textarea
              name="when_to_recommend"
              value={formData.when_to_recommend}
              onChange={handleInputChange}
              placeholder="Describe the conditions, biomarker levels, or patient profiles when this tactic should be recommended..."
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* When Not to Recommend */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              When Not to Recommend
            </label>
            <textarea
              name="when_not_to_recommend"
              value={formData.when_not_to_recommend}
              onChange={handleInputChange}
              placeholder="Describe contraindications, conditions, or patient profiles when this tactic should NOT be recommended..."
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* References */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              References
            </label>
            <textarea
              name="references"
              value={formData.references}
              onChange={handleInputChange}
              placeholder="Add references, links, or citations for this tactic..."
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
            >
              {mode === 'create' ? 'Create Tactic' : 'Update Tactic'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TacticsModal;
